// AGC: TypeScript types for Admin Panel Models Configuration
// AGC: Defines all configuration types for Meteorology and Crop Recommendation algorithms

// ============================================================================
// METEOROLOGY CONFIGURATION TYPES
// ============================================================================

/**
 * AGC: Spray application thresholds based on international agronomic standards
 * AGC: Used to determine optimal conditions for pesticide/herbicide application
 */
export interface SprayThresholds {
  wind: {
    min: number        // AGC: km/h - minimum ideal (below: drift risk from poor dispersion)
    max: number        // AGC: km/h - maximum ideal (above: drift risk from wind)
    critical: number   // AGC: km/h - do not spray threshold
  }
  temperature: {
    min: number        // AGC: °C - minimum for effective application
    max: number        // AGC: °C - maximum for effective application
    critical: number   // AGC: °C - do not spray threshold
  }
  humidity: {
    min: number        // AGC: % - minimum (below: evaporation risk)
    max: number        // AGC: % - maximum (above: runoff risk)
  }
  deltaT: {
    min: number        // AGC: °C - minimum delta-T (below: thermal inversion)
    max: number        // AGC: °C - maximum delta-T (above: excessive evaporation)
    critical: number   // AGC: °C - critical threshold
  }
  rain: {
    checkHours: number           // AGC: hours ahead to check rain forecast
    probabilityThreshold: number // AGC: % probability to trigger alert
  }
}

/**
 * AGC: Weights for health score calculation
 * AGC: Based on ZARC methodology for crop climate suitability
 */
export interface HealthScoreWeights {
  precipitation: number  // AGC: 0-1, default 0.45 - most impactful factor
  temperature: number    // AGC: 0-1, default 0.35 - critical for growth stages
  radiation: number      // AGC: 0-1, default 0.20 - photosynthesis efficiency
}

/**
 * AGC: Health score threshold definition
 * AGC: Defines visual status levels for lot health
 */
export interface HealthThreshold {
  label: string    // AGC: Display label (e.g., "Ótimo", "Atenção")
  min: number      // AGC: Minimum score for this level
  color: string    // AGC: Hex color for UI display
}

/**
 * AGC: Cache configuration for weather data
 * AGC: Optimizes API usage and supports offline mode
 */
export interface CacheConfig {
  durationMinutes: number   // AGC: How long to cache weather data
  offlineFallback: boolean  // AGC: Use cached data when offline
}

/**
 * AGC: Complete meteorology configuration
 * AGC: Encompasses all weather-related algorithm parameters
 */
export interface MeteorologiaConfig {
  spray: SprayThresholds
  healthScore: {
    weights: HealthScoreWeights
    thresholds: HealthThreshold[]
  }
  cache: CacheConfig
  updatedAt: string   // AGC: ISO timestamp of last update
  updatedBy: string   // AGC: User ID who made the update
}

// ============================================================================
// RECOMMENDATIONS CONFIGURATION TYPES
// ============================================================================

/**
 * AGC: Algorithm weight distribution for crop recommendations
 * AGC: Based on EMBRAPA multi-criteria decision methodology
 */
export interface RecommendationWeights {
  climate: number    // AGC: 0-1, default 0.30 - climate suitability
  soil: number       // AGC: 0-1, default 0.25 - soil compatibility
  rotation: number   // AGC: 0-1, default 0.20 - crop rotation benefits
  economic: number   // AGC: 0-1, default 0.15 - market and cost factors
  temporal: number   // AGC: 0-1, default 0.10 - planting window timing
}

/**
 * AGC: Growth stage definition for crop phenology
 */
export interface GrowthStage {
  code: string       // AGC: Stage code (e.g., "V1", "R1")
  name: string       // AGC: Stage name (e.g., "Emergência", "Floração")
  startDay: number   // AGC: Days from planting when stage begins
}

/**
 * AGC: Crop climate requirements
 * AGC: Based on EMBRAPA technical bulletins and FAO data
 */
export interface CropRequirements {
  id: string                    // AGC: Unique crop identifier
  name: string                  // AGC: Display name
  icon: string                  // AGC: Emoji icon for UI
  color: string                 // AGC: Brand color (hex)
  precipitationTotal: number    // AGC: mm required for full cycle
  tempOptimalMin: number        // AGC: °C minimum optimal temperature
  tempOptimalMax: number        // AGC: °C maximum optimal temperature
  radiationTotal: number        // AGC: MJ/m² required for full cycle
  projectedYield: number        // AGC: t/ha expected yield
  cycleDays: number             // AGC: Days for complete cycle
  growthStages: GrowthStage[]   // AGC: Phenological stages
  imageUrl?: string             // AGC: Full HD crop photo URL (for LOD card backgrounds)
  imageUpdatedAt?: string       // AGC: ISO timestamp for cache invalidation
}

/**
 * AGC: ZARC-based penalty factor
 * AGC: Reduces score based on risk conditions from MAPA/ZARC data
 */
export interface ZarcPenalty {
  id: string                                              // AGC: Unique identifier
  label: string                                           // AGC: Display label
  value: number                                           // AGC: Points to subtract (negative)
  description: string                                     // AGC: Explanation text
  category: 'temperature' | 'precipitation' | 'risk_level' | 'rotation'
}

/**
 * AGC: General recommendation algorithm settings
 */
export interface RecommendationGeneralConfig {
  minScoreToRecommend: number   // AGC: Minimum score to show crop (default 50)
  maxRecommendations: number    // AGC: Maximum crops to recommend (default 5)
}

/**
 * AGC: Complete crop recommendation configuration
 */
export interface RecomendacoesConfig {
  weights: RecommendationWeights
  general: RecommendationGeneralConfig
  crops: CropRequirements[]
  zarcPenalties: ZarcPenalty[]
  updatedAt: string
  updatedBy: string
}

// ============================================================================
// TASK TEMPLATES CONFIGURATION TYPES
// ============================================================================

/**
 * AGC: Task category for organizing farm operations
 */
export type TaskCategory = 'preparation' | 'planting' | 'spraying' | 'fertilizing' | 'harvest' | 'monitoring' | 'other'

/**
 * AGC: Task input - links an input (insumo) to a task template
 * AGC: Defines the quantity of input required per hectare for a task
 */
export interface TaskInput {
  inputId: string                 // AGC: Reference to InputMaster catalog
  inputName: string               // AGC: Denormalized name for display
  category: string                // AGC: Input category (herbicide, fertilizer, etc.)
  quantityPerHa: number           // AGC: Required quantity per hectare
  unit: string                    // AGC: Unit of measurement (L, kg, etc.)
  notes?: string                  // AGC: Application notes or instructions
}

/**
 * AGC: Task output — declares what a template task produces (e.g. grain harvest).
 * AGC: Mirrors TaskInput shape but represents production rather than consumption.
 */
export interface TaskOutput {
  outputId: string                 // AGC: stable id, e.g. 'trigo_grao'
  outputName: string               // AGC: display name, e.g. 'Trigo grão'
  category: string                 // AGC: e.g. 'grão' — discriminator for storage rules
  expectedYieldPerHa: number       // AGC: e.g. 3500 (kg/ha for trigo)
  unit: string                     // AGC: 'kg', 'sacas', etc.
}

/**
 * AGC: Task template - defines a farm operation for a specific crop
 * AGC: Stored in Firestore: config/taskTemplates/{cropId}/tasks/{taskId}
 */
export interface TaskTemplate {
  id: string
  cropId: string                  // AGC: Which crop this task belongs to
  title: string                   // AGC: Task name (e.g., "Aplicação de Herbicida")
  category: TaskCategory          // AGC: Task category
  icon: string                    // AGC: Emoji icon for UI
  phenologicalStageCode?: string  // AGC: Which growth stage (e.g., "VE", "V6", "R1")
  daysFromPlanting: number        // AGC: Days after planting when task should be done
  duration?: number               // AGC: Estimated duration in hours
  description?: string            // AGC: Detailed task description
  inputs: TaskInput[]             // AGC: Required inputs for this task
  outputs?: TaskOutput[]          // AGC: NEW (Phase 1 grain-output spec) — produced items per ha
  order: number                   // AGC: Display order within crop
  isRequired: boolean             // AGC: Whether task is mandatory
  isActive: boolean               // AGC: Can be deactivated without deletion
  createdAt: string
  updatedAt: string
}

/**
 * AGC: Phenological stage - defines crop growth stages
 * AGC: Based on standard agronomic scales (BBCH, etc.)
 * AGC: Stored in Firestore: config/phenologicalStages/{cropId}/stages/{stageId}
 */
export interface PhenologicalStage {
  id: string
  cropId: string                  // AGC: Which crop this stage belongs to
  code: string                    // AGC: Standard stage code (e.g., "VE", "V1", "R1")
  name: string                    // AGC: Stage name in Portuguese
  nameEn?: string                 // AGC: Stage name in English
  nameEs?: string                 // AGC: Stage name in Spanish
  daysFromPlanting: number        // AGC: Typical days from planting to reach stage
  daysRange?: {                   // AGC: Range of days (varies by variety/conditions)
    min: number
    max: number
  }
  description?: string            // AGC: What happens at this stage
  criticalFactors?: string[]      // AGC: Key factors to monitor (water, nutrients, etc.)
  imagePath?: string              // AGC: Path to reference image
  order: number                   // AGC: Display order
  createdAt: string
  updatedAt: string
}

/**
 * AGC: Task templates configuration per crop
 */
export interface CropTaskTemplates {
  cropId: string
  cropName: string
  cropIcon: string
  tasks: TaskTemplate[]
  stages: PhenologicalStage[]
  updatedAt: string
  updatedBy: string
}

// ============================================================================
// COMBINED CONFIG TYPE
// ============================================================================

/**
 * AGC: Combined models configuration
 * AGC: Top-level type for all algorithm configurations
 */
export interface ModelsConfig {
  meteorologia: MeteorologiaConfig
  recomendacoes: RecomendacoesConfig
  taskTemplates?: Record<string, CropTaskTemplates>
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

/**
 * AGC: Default meteorology configuration
 * AGC: Values based on international agronomic standards
 */
export const DEFAULT_METEOROLOGIA_CONFIG: MeteorologiaConfig = {
  spray: {
    wind: { min: 3, max: 15, critical: 20 },
    temperature: { min: 10, max: 30, critical: 35 },
    humidity: { min: 50, max: 95 },
    deltaT: { min: 2, max: 8, critical: 10 },
    rain: { checkHours: 4, probabilityThreshold: 50 },
  },
  healthScore: {
    weights: { precipitation: 0.45, temperature: 0.35, radiation: 0.20 },
    thresholds: [
      { label: 'Ótimo', min: 90, color: '#22c55e' },
      { label: 'Atenção', min: 70, color: '#eab308' },
      { label: 'Moderado', min: 50, color: '#f97316' },
      { label: 'Crítico', min: 0, color: '#ef4444' },
    ],
  },
  cache: { durationMinutes: 30, offlineFallback: true },
  updatedAt: new Date().toISOString(),
  updatedBy: 'system',
}

/**
 * AGC: Default crop requirements based on EMBRAPA data
 */
export const DEFAULT_CROPS: CropRequirements[] = [
  {
    id: 'soja',
    name: 'Soja',
    icon: '🫘',
    color: '#10B981',
    precipitationTotal: 450,
    tempOptimalMin: 20,
    tempOptimalMax: 30,
    radiationTotal: 2400,
    projectedYield: 3.2,
    cycleDays: 120,
    growthStages: [
      { code: 'VE', name: 'Emergência', startDay: 0 },
      { code: 'V1', name: 'Primeiro nó', startDay: 10 },
      { code: 'V3', name: 'Terceiro nó', startDay: 20 },
      { code: 'R1', name: 'Início floração', startDay: 45 },
      { code: 'R5', name: 'Enchimento grãos', startDay: 75 },
      { code: 'R8', name: 'Maturação', startDay: 110 },
    ],
  },
  {
    id: 'milho',
    name: 'Milho',
    icon: '🌽',
    color: '#F59E0B',
    precipitationTotal: 550,
    tempOptimalMin: 18,
    tempOptimalMax: 32,
    radiationTotal: 2800,
    projectedYield: 5.5,
    cycleDays: 140,
    growthStages: [
      { code: 'VE', name: 'Emergência', startDay: 0 },
      { code: 'V6', name: 'Sexta folha', startDay: 25 },
      { code: 'VT', name: 'Pendoamento', startDay: 55 },
      { code: 'R1', name: 'Embonecamento', startDay: 60 },
      { code: 'R3', name: 'Grão leitoso', startDay: 80 },
      { code: 'R6', name: 'Maturação', startDay: 130 },
    ],
  },
  {
    id: 'trigo',
    name: 'Trigo',
    icon: '🌾',
    color: '#B45309',
    precipitationTotal: 350,
    tempOptimalMin: 10,
    tempOptimalMax: 25,
    radiationTotal: 2000,
    projectedYield: 3.0,
    cycleDays: 130,
    growthStages: [
      { code: 'GS10', name: 'Emergência', startDay: 0 },
      { code: 'GS20', name: 'Perfilhamento', startDay: 20 },
      { code: 'GS30', name: 'Alongamento', startDay: 45 },
      { code: 'GS60', name: 'Floração', startDay: 75 },
      { code: 'GS80', name: 'Maturação', startDay: 110 },
    ],
  },
  {
    id: 'algodao',
    name: 'Algodão',
    icon: '☁️',
    color: '#6366F1',
    precipitationTotal: 700,
    tempOptimalMin: 18,
    tempOptimalMax: 35,
    radiationTotal: 3500,
    projectedYield: 4.5,
    cycleDays: 180,
    growthStages: [
      { code: 'VE', name: 'Emergência', startDay: 0 },
      { code: 'V5', name: 'Quinta folha', startDay: 25 },
      { code: 'B1', name: 'Primeiro botão', startDay: 40 },
      { code: 'F1', name: 'Primeira flor', startDay: 60 },
      { code: 'C1', name: 'Primeiro capulho', startDay: 120 },
      { code: 'M', name: 'Maturação', startDay: 170 },
    ],
  },
  {
    id: 'feijao',
    name: 'Feijão',
    icon: '🫘',
    color: '#7C3AED',
    precipitationTotal: 300,
    tempOptimalMin: 15,
    tempOptimalMax: 28,
    radiationTotal: 1800,
    projectedYield: 2.5,
    cycleDays: 90,
    growthStages: [
      { code: 'V0', name: 'Germinação', startDay: 0 },
      { code: 'V2', name: 'Folhas primárias', startDay: 8 },
      { code: 'V4', name: 'Terceiro trifólio', startDay: 20 },
      { code: 'R5', name: 'Pré-floração', startDay: 35 },
      { code: 'R7', name: 'Formação vagens', startDay: 50 },
      { code: 'R9', name: 'Maturação', startDay: 85 },
    ],
  },
  {
    id: 'arroz',
    name: 'Arroz',
    icon: '🍚',
    color: '#84CC16',
    precipitationTotal: 1200,
    tempOptimalMin: 22,
    tempOptimalMax: 32,
    radiationTotal: 2600,
    projectedYield: 4.0,
    cycleDays: 120,
    growthStages: [
      { code: 'S0', name: 'Germinação', startDay: 0 },
      { code: 'V3', name: 'Perfilhamento', startDay: 20 },
      { code: 'V7', name: 'Elongação', startDay: 45 },
      { code: 'R2', name: 'Emborrachamento', startDay: 65 },
      { code: 'R5', name: 'Floração', startDay: 80 },
      { code: 'R9', name: 'Maturação', startDay: 115 },
    ],
  },
]

/**
 * AGC: Default ZARC penalties based on MAPA guidelines
 */
export const DEFAULT_ZARC_PENALTIES: ZarcPenalty[] = [
  {
    id: 'zarc_baixo',
    label: 'Risco ZARC Baixo (20%)',
    value: -5,
    description: 'Condições dentro da janela de plantio recomendada',
    category: 'risk_level',
  },
  {
    id: 'zarc_medio',
    label: 'Risco ZARC Médio (30%)',
    value: -15,
    description: 'Condições parcialmente favoráveis',
    category: 'risk_level',
  },
  {
    id: 'zarc_alto',
    label: 'Risco ZARC Alto (40%)',
    value: -40,
    description: 'Risco elevado de perda - fora da janela ZARC',
    category: 'risk_level',
  },
  {
    id: 'temp_fora_faixa',
    label: 'Temperatura fora da faixa ótima',
    value: -20,
    description: 'Temperatura média fora dos limites ideais do cultivo',
    category: 'temperature',
  },
  {
    id: 'precip_deficit_leve',
    label: 'Déficit precipitação leve (50-70%)',
    value: -15,
    description: 'Precipitação abaixo do ideal, mas viável com irrigação',
    category: 'precipitation',
  },
  {
    id: 'precip_deficit_severo',
    label: 'Déficit precipitação severo (>70%)',
    value: -30,
    description: 'Precipitação muito abaixo do necessário',
    category: 'precipitation',
  },
  {
    id: 'precip_excesso',
    label: 'Excesso precipitação (>150%)',
    value: -15,
    description: 'Risco de encharcamento e doenças',
    category: 'precipitation',
  },
  {
    id: 'rotacao_inadequada',
    label: 'Rotação inadequada',
    value: -25,
    description: 'Mesmo cultivo repetido ou família incompatível',
    category: 'rotation',
  },
]

/**
 * AGC: Default recommendation configuration
 */
export const DEFAULT_RECOMENDACOES_CONFIG: RecomendacoesConfig = {
  weights: {
    climate: 0.30,
    soil: 0.25,
    rotation: 0.20,
    economic: 0.15,
    temporal: 0.10,
  },
  general: {
    minScoreToRecommend: 50,
    maxRecommendations: 5,
  },
  crops: DEFAULT_CROPS,
  zarcPenalties: DEFAULT_ZARC_PENALTIES,
  updatedAt: new Date().toISOString(),
  updatedBy: 'system',
}

// ============================================================================
// AGC: RECOMMENDATION MODEL VERSIONING TYPES (Phase 2)
// ============================================================================

export interface RecommendationModelVersion {
  versionId: string
  label: string
  isActive: boolean
  createdAt: string
  createdBy: string
  notes: string
  cropRequirements?: Record<string, unknown>
  zarcWeights?: Record<string, number>
  taskTemplates?: Record<string, unknown>
  yieldModels?: Record<string, unknown>
}

// ============================================================================
// AGC: YIELD MODEL TYPES (Phase 3)
// ============================================================================

export interface CropYieldModel {
  baseYieldTnHa: number
  minYieldTnHa: number
  maxYieldTnHa: number
  scoreThresholds: {
    excelente: number
    bom: number
    moderado: number
  }
  yieldAdjustmentCurve: 'linear' | 'exponential'
}

export interface ZarcWeightsConfig {
  precipitation: number
  temperature: number
  radiation: number
  season: number
}

export const DEFAULT_ZARC_WEIGHTS: ZarcWeightsConfig = {
  precipitation: 0.35,
  temperature: 0.35,
  radiation: 0.10,
  season: 0.20,
}

// ============================================================================
// AGC: ARGENTINA MODEL CONFIG (per-country, admin-editable, auditable)
// ============================================================================

export interface ArgentinaWeights {
  water_balance: number          // AGC: default 0.35 — ORA Kc x ET0 vs precipitation
  season_match: number           // AGC: default 0.30 — planting-window alignment vs ORA calendar
  thermal_match: number          // AGC: default 0.20 — DEFERRED (cited INTA thresholds)
  soil_match: number             // AGC: default 0.10 — DEFERRED (GeoINTA AWC)
  historical_consistency: number // AGC: default 0.05 — DEFERRED (MAGyP series)
}

export interface ArgentinaBaseYieldEntry {
  value: number   // AGC: kg/ha base/reference yield
  unit: string    // AGC: 'kg/ha'
  source: string  // AGC: REQUIRED citation (NO INVENTAR) e.g. 'MAGyP 2018-2023 avg'
}

// AGC: [AR][RIEGO] Yield uplift applied when the lot is irrigated (editable estimate, cited).
export interface ArgentinaIrrigationFactor {
  value: number   // AGC: multiplier on projected yield when irrigated (e.g. 1.15 = +15%)
  source: string  // AGC: citation / note — editable default, refine with INTA riego data (NO INVENTAR)
}

export interface ArgentinaModelConfigType {
  crops: string[]                                                  // AGC: e.g. ['soja','maiz','trigo','girasol','algodon']
  weights: ArgentinaWeights
  min_score_threshold?: number                                     // AGC: score >= threshold tags crop as recommended (default 55)
  irrigation_yield_factor?: ArgentinaIrrigationFactor             // AGC: [AR][RIEGO] yield uplift when irrigated (default 1.15)
  base_yield: Record<string, Record<string, ArgentinaBaseYieldEntry>> // AGC: [crop][zoneCode] -> entry
  updatedAt?: string
  updatedBy?: string
}

export const DEFAULT_ARGENTINA_MODEL_CONFIG: ArgentinaModelConfigType = {
  crops: ['soja', 'maiz', 'trigo', 'girasol', 'algodon'],
  weights: { water_balance: 0.35, season_match: 0.30, thermal_match: 0.20, soil_match: 0.10, historical_consistency: 0.05 },
  min_score_threshold: 55,
  irrigation_yield_factor: { value: 1.15, source: 'Estimación editable — ajustar con datos INTA de riego suplementario' },
  base_yield: {},
}
